import { isReleaseCategory } from '../../helpers';
import { ChangelogProps } from '../../types';
import { ChevronRightIcon } from '@heroicons/react/24/solid';

const parseRichText = (text: string): React.ReactNode[] => {
  const regex = /(\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\))/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  text.replace(
    regex,
    (match: string, _: unknown, boldText: string, linkText: string, linkUrl: string, index: number) => {
      if (lastIndex < index) {
        result.push(text.slice(lastIndex, index));
      }

      // Find bold texts
      if (boldText) {
        result.push(<strong key={index}>{boldText}</strong>);
      }

      // Find links
      if (linkText && linkUrl) {
        result.push(
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" key={index}>
            {linkText}
          </a>,
        );
      }

      lastIndex = index + match.length;
      return '';
    },
  );

  // Add text left
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
};

const customEntry = (entry: string): React.ReactNode[] => {
  return (<span>{parseRichText(entry)}</span>) as unknown as React.ReactNode[];
};

const BulletItem: React.FC<{ children: React.ReactNode; isSubitem?: boolean }> = ({
  children,
  isSubitem = false,
}) => (
  <>
    {isSubitem ? (
      <li className="sub-bullet row ml-4">
        <ChevronRightIcon style={{ width: '1rem', color: 'var(--pink-color)', marginRight: '0.25rem' }} />
        {children}
      </li>
    ) : (
      <li className="bullet-item">{children}</li>
    )}
  </>
);

const Changelog: React.FC<ChangelogProps> = ({ changelog }) => {
  const filteredChangelog = Object.entries(changelog).filter(
    ([_, entries]) => Array.isArray(entries) && entries.length > 0,
  );

  return (
    <div>
      {filteredChangelog.map(([category, entries]) => (
        <div key={category} className="my-4">
          {isReleaseCategory(category) && <h2 className="version-number">{category.split('-')[1]}</h2>}
          {category && !category.includes('release') && <h6>{category}:</h6>}
          {entries.map((entry, idx) => {
            const isParagraph = typeof entry === 'string' && entry.startsWith('p::');
            const isSubitem = typeof entry === 'string' && entry.startsWith('sub::');

            let rawText = entry as string;
            if (isParagraph) {
              rawText = entry.slice(3);
            } else if (isSubitem) {
              rawText = entry.slice(5);
            }

            const parsed = customEntry(rawText);

            if (isParagraph) {
              return (
                <p key={idx} className="mb-0">
                  {parsed}
                </p>
              );
            }

            if (isSubitem) {
              return (
                <ul key={idx} className="mb-1">
                  <BulletItem isSubitem>{parsed}</BulletItem>
                </ul>
              );
            }

            return <BulletItem key={idx}>{parsed}</BulletItem>;
          })}
        </div>
      ))}
    </div>
  );
};

export default Changelog;
